import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import CrossIcon from '@atlaskit/icon/core/cross';
import Link from '@atlaskit/link/link';
import { RovoIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getLocalStorageItem, setLocalStorageItem } from '@atlassian/search-common/local-storage';

import { onBannerDismissed } from '../../utils/events/banner-dismissed';
import { onBannerShown } from '../../utils/events/banner-shown';
import { onBrowserExtensionCTAClicked } from '../../utils/events/browser-extension-cta-clicked';
import { useAppContext } from '../app-context';
import { messages } from './messages';

const styles = cssMap({
	banner: {
		position: 'relative',
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		minHeight: '108px',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		marginBlock: token('space.100'),
		backgroundImage: `conic-gradient(from 180deg at 50% 50%,
            rgba(252, 167, 0, 0.05) 1.8deg,
            rgba(130, 181, 54, 0.05) 70.2deg,
            rgba(24, 104, 219, 0.05) 128.85deg,
            rgba(191, 99, 243, 0.05) 223.2deg,
            rgba(240, 241, 242, 0.05) 316.8deg,
            rgba(252, 167, 0, 0.05) 352.8deg)`,
	},
	content: {
		position: 'relative',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
	},
	header: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.150'),
		marginBottom: token('space.100'),
		flexShrink: 0,
	},
	title: {
		font: token('font.heading.xsmall'),
		fontWeight: token('font.weight.semibold'),
		color: token('color.text'),
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		wordWrap: 'break-word',
		overflowWrap: 'break-word',
		hyphens: 'auto',
	},
	closeButtonContainer: {
		position: 'absolute',
		top: token('space.0'),
		right: token('space.0'),
		zIndex: 1,
	},
	linkContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		flexShrink: 0,
		marginTop: 'auto',
	},
});

const isGoogleChromeBrowser = (): boolean => {
	if (typeof navigator === 'undefined') {
		return false;
	}

	const uaData = (navigator as any).userAgentData;
	const brands: string[] = uaData?.brands?.map((b: { brand: string }) => b.brand) ?? [];

	if (brands.length) {
		const result =
			brands.includes('Google Chrome') && !brands.some((b) => /(Microsoft Edge|Opera)/i.test(b));
		return result;
	}

	const ua = navigator.userAgent || '';
	const isDesktopChrome = /Chrome\/\d+/.test(ua) && !/(Edg|OPR|Chromium)\//.test(ua);
	const isIOS = /\b(iPad|iPhone|iPod)\b/.test(ua) || /CriOS\/\d+/.test(ua);
	return isDesktopChrome && !isIOS;
};

declare global {
	interface Window {
		rovoExtension?: {
			openChat?: () => void;
		};
	}
}

const isRovoExtensionInstalled = (): boolean => {
	try {
		// Check for the rovoExtension object that the extension injects
		return typeof window?.rovoExtension?.openChat === 'function';
	} catch {
		return false;
	}
};

interface BannerState {
	isCTAClicked: boolean;
	isDismissed: boolean;
}

const STORAGE_KEY = 'search-page-rovo-browser-extension-banner-state';

const getBannerState = (): BannerState => {
	const state = getLocalStorageItem(STORAGE_KEY);
	return state || { isCTAClicked: false, isDismissed: false };
};

const setBannerState = (updates: Partial<BannerState>) => {
	const currentState = getBannerState();
	const newState = { ...currentState, ...updates };
	setLocalStorageItem(STORAGE_KEY, JSON.stringify(newState));
};

const hasUserDismissedBanner = (): boolean => {
	return getBannerState().isDismissed;
};

const hasUserClickedCTA = (): boolean => {
	return getBannerState().isCTAClicked;
};

export const useBrowserExtensionBannerVisibility: () => {
	isVisible: boolean;
	markAsDismissed: () => void;
	markCTAClicked: () => void;
} = () => {
	const [isVisible, setIsVisible] = useState(false);
	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();

	useEffect(() => {
		// Check if AI/LLM features are enabled based on entitlements
		const areLLMPoweredFeaturesEnabled = fg('rovo_search_updated_entitlement_checks_for_team_25')
			? isRovoLLMEnabled
			: isAdminHubAIEnabled;

		const extensionInstalled = isRovoExtensionInstalled();
		const userDismissed = hasUserDismissedBanner();
		const userClickedCTA = hasUserClickedCTA();
		const isChrome = isGoogleChromeBrowser();

		const shouldShow =
			areLLMPoweredFeaturesEnabled &&
			isChrome &&
			!extensionInstalled &&
			!userDismissed &&
			!userClickedCTA &&
			FeatureGates.getExperimentValue<boolean>(
				'rovo-expansion-fps-extension-install-exp',
				'isEnabled',
				false,
			);

		setIsVisible(shouldShow);
	}, [isAdminHubAIEnabled, isRovoLLMEnabled]);

	const markAsDismissed = (): void => {
		setBannerState({ isDismissed: true });
		setIsVisible(false);
	};

	const markCTAClicked = (): void => {
		setBannerState({ isCTAClicked: true });
		setIsVisible(false);
	};

	return {
		isVisible,
		markAsDismissed,
		markCTAClicked,
	};
};

export type BrowserExtensionBannerProps = {
	testId?: string;
	fireAnalyticsEvent?: (payload: any) => void;
};

/**
 * BrowserExtensionBanner - Promotes the Rovo browser extension
 *
 * This banner will only display when ALL of the following conditions are met:
 * 1. User has access to AI/Rovo features (based on entitlements and application mode)
 * 2. Browser is Chrome/Chromium/Edge
 * 3. Rovo extension is not installed
 * 4. User hasn't dismissed the banner (stored in localStorage)
 * 5. User hasn't clicked the CTA link (stored in localStorage)
 *
 * The banner state is stored using a single localStorage key with an object structure.
 */
export const BrowserExtensionBanner: ({
	testId,
	fireAnalyticsEvent,
}: BrowserExtensionBannerProps) => React.JSX.Element | null = ({
	testId = 'browser-extension-banner',
	fireAnalyticsEvent,
}: BrowserExtensionBannerProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { isVisible, markAsDismissed, markCTAClicked } = useBrowserExtensionBannerVisibility();

	const handleDismiss = () => {
		markAsDismissed();

		// Fire analytics event for banner dismissal
		if (fireAnalyticsEvent) {
			fireAnalyticsEvent(
				onBannerDismissed({
					componentName: 'BrowserExtensionBanner',
				}),
			);
		}
	};

	const handleCTAClick = () => {
		markCTAClicked();

		// Fire analytics event for CTA click
		if (fireAnalyticsEvent) {
			fireAnalyticsEvent(onBrowserExtensionCTAClicked());
		}
	};

	useEffect(() => {
		if (isVisible && fireAnalyticsEvent) {
			fireAnalyticsEvent(
				onBannerShown({
					componentName: 'BrowserExtensionBanner',
				}),
			);
		}
	}, [fireAnalyticsEvent, isVisible]);

	if (!isVisible) {
		return null;
	}

	return (
		<Box xcss={styles.banner} testId={testId}>
			<Box xcss={styles.content}>
				<Box xcss={styles.closeButtonContainer}>
					<IconButton
						spacing="compact"
						appearance="subtle"
						icon={() => <CrossIcon size="small" label="" />}
						label={formatMessage(messages.closeButtonLabel)}
						onClick={handleDismiss}
						testId={`${testId}-close`}
					/>
				</Box>

				<Box xcss={styles.header}>
					<RovoIcon appearance="brand" size="small" />
					<Box xcss={styles.title}>{formatMessage(messages.title)}</Box>
				</Box>

				<Box xcss={styles.linkContainer}>
					<Link
						href="https://chromewebstore.google.com/detail/npbjhobkibekbklkghlhfhieggcggpdb?utm_source=confluence-full-page-search&utm_medium=persistent-banner&utm_campaign=rovo_extension_confluence_fps_install_cta"
						target="_blank"
						appearance="default"
						testId={`${testId}-link`}
						onClick={handleCTAClick}
					>
						{formatMessage(messages.linkText)}
					</Link>
				</Box>
			</Box>
		</Box>
	);
};
